/* eslint-disable @typescript-eslint/no-deprecated */

import React, { useState } from 'react'
import styled from 'styled-components'
import { Field, FieldInputProps, useFormikContext, useField } from 'formik'
import { Body, SmallBody } from '../../buildingBlocks'
import { WarningIcon } from './WarningIcon'

export interface TextAreaWithSuggestionsProps extends Omit<
  FieldInputProps<React.HTMLProps<HTMLTextAreaElement>>,
  'value' | 'onChange' | 'onBlur'
> {
  placeholder?: string
  suggestions?: string[]
  charLimit?: number
  showValidationErrorMessages?: boolean
}

interface TextAreaState {
  focus: boolean
  hasError: boolean
}

export const TextAreaWithSuggestions: React.FC<TextAreaWithSuggestionsProps> = ({
  suggestions,
  showValidationErrorMessages = true,
  charLimit,
  name,
  ...rest
}) => {
  const { setFieldValue, getFieldMeta } = useFormikContext()
  const [field] = useField(name)
  const [focus, setFocus] = useState(false)
  const { error } = getFieldMeta(name)
  const hasError = showValidationErrorMessages && error !== undefined
  const textAreaState: TextAreaState = {
    focus,
    hasError,
  }
  const charLimitText = charLimit && formatCharLimitText(field.value.length, charLimit)

  return (
    <>
      <Container {...textAreaState}>
        {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
        {/* @ts-ignore */}
        <TextArea
          {...rest}
          name={name}
          id={name}
          showValidationErrorMessages={showValidationErrorMessages}
          value={field.value}
          as="textarea"
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFieldValue(field.name, e.target.value)}
          onFocus={() => setFocus(true)}
          onBlur={() => setFocus(false)}
          rows={5}
        />
        <Suggestions>
          {suggestions?.map((suggestion) => (
            <SuggestionButton
              {...textAreaState}
              type="button"
              onClick={() => setFieldValue(field.name, suggestion)}
              key={suggestion}
            >
              <SmallBody m={0}>{suggestion}</SmallBody>
            </SuggestionButton>
          ))}
        </Suggestions>
        {hasError && <WarningIcon indent={1} top={1} />}
        <CharacterCount isError={hasError}>
          <span>{charLimitText}</span>
        </CharacterCount>
      </Container>
      <HelperText isError={hasError}>
        <span>{error}</span>
      </HelperText>
    </>
  )
}

const formatCharLimitText = (currentLength: number, limit: number) => `${currentLength} / ${limit}`

const Container = styled.div<TextAreaState>`
  border: solid 1px;
  border-color: ${({ theme, focus, hasError }) =>
    hasError ? theme.colors.danger.dark : focus ? theme.colors.success.dark : theme.colors.grey.medium};
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 5px;
  display: flex;
  flex-direction: column;
  padding: 1rem 5rem 1rem 1rem;
  position: relative;
`

const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
`

const TextArea = styled(Field)`
  border: none;
  resize: none;
  outline: none;
  font-family: inherit;
  font-size: 1.6rem;
  font-style: italic;
`

const CharacterCount = styled(Body)<{ isError: boolean }>`
  font-size: 1.3rem;
  position: absolute;
  color: ${(p) => (p.isError ? p.theme.colors.danger.dark : p.theme.colors.dark)};
  right: 2.9rem;
  bottom: -0.7rem;
  line-height: 1.3rem;
  background-color: white;
  margin: 0;
  padding: 0;
`

const HelperText = styled(Body)<{ isError: boolean }>`
  font-size: 1.3rem;
  color: ${(p) => (p.isError ? p.theme.colors.danger.dark : p.theme.colors.dark)};
  left: 2.9rem;
  line-height: 1.3rem;
  display: flex;
  padding: 1rem 0 0;
  margin: 0;
`

const SuggestionButton = styled.button<TextAreaState>`
  margin: 8px 8px 0 0;
  border: solid 1px;
  border-color: ${({ theme, focus, hasError }) =>
    hasError ? theme.colors.danger.dark : focus ? theme.colors.success.dark : theme.colors.grey.medium};
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.background.light};
  cursor: pointer;
  border-radius: 5px;
  p {
    font-weight: normal;
    color: ${({ theme }) => theme.colors.grey.dark};
  }
`
